import { useForm, useList } from '@refinedev/core';
import { useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormField, FormHeader, FormSpacer, FormStickyActions } from '@/components/forms';
import {
  TranslationsForm,
  LanguageSwitcher,
  createEmptyTranslationData,
  type TranslationData,
  type TranslationFieldConfig,
  type TranslationLanguage,
  type LanguageCode,
} from '@/components/translations';
import { Loader2, FileText, Settings } from 'lucide-react';
import type { LegalPage, LegalPageFormData } from './types';

const TRANSLATION_FIELDS: TranslationFieldConfig[] = [
  { name: 'title', label: 'Заголовок', type: 'input', required: true },
  { name: 'body', label: 'Содержимое', type: 'richtext' },
];

type LanguageRecord = {
  code: string;
  name: string;
  nativeName: string;
  flag?: string | null;
  flagEmoji?: string | null;
};

function buildTranslationData(page?: LegalPage): TranslationData {
  const translations = createEmptyTranslationData();
  if (!page) {
    return translations;
  }
  for (const t of page.translations ?? []) {
    translations[t.languageCode] = {
      title: t.title ?? '',
      body: t.body ?? '',
    };
  }
  return translations;
}

export function LegalPagesForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { formLoading, onFinish, query } = useForm<LegalPage>({
    resource: 'legal-pages',
    action: isEdit ? 'edit' : 'create',
    id,
    redirect: 'list',
  });

  const { result: languagesResult, query: langQuery } = useList<LanguageRecord>({
    resource: 'languages',
    pagination: { pageSize: 100 },
  });

  const isLanguagesLoading = langQuery?.isLoading ?? false;
  const languages: TranslationLanguage[] | undefined =
    languagesResult?.data?.map((l) => ({
      code: l.code,
      name: l.name,
      nativeName: l.nativeName,
      flag: l.flag || l.flagEmoji || '',
    }));

  const page = query?.data?.data;
  const formKey = isEdit ? `edit-${page?.id ?? 'loading'}` : 'create';

  if (isEdit && (formLoading || isLanguagesLoading)) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <LegalPagesFormFields
      key={formKey}
      isEdit={isEdit}
      formLoading={formLoading}
      page={page}
      languages={languages}
      onFinish={async (v) => {
        await onFinish(v);
      }}
      onBack={() => navigate(-1)}
    />
  );
}

interface LegalPagesFormFieldsProps {
  isEdit: boolean;
  formLoading: boolean;
  page?: LegalPage;
  languages?: TranslationLanguage[];
  onFinish: (values: LegalPageFormData) => Promise<void>;
  onBack: () => void;
}

function LegalPagesFormFields({
  isEdit,
  formLoading,
  page,
  languages,
  onFinish,
  onBack,
}: LegalPagesFormFieldsProps) {
  const [slug, setSlug] = useState(page?.slug ?? '');
  const [isPublished, setIsPublished] = useState(page?.isPublished ?? false);
  const [translations, setTranslations] = useState<TranslationData>(
    buildTranslationData(page),
  );
  const [activeLanguage, setActiveLanguage] = useState<LanguageCode>('ru');

  const handleSubmit = async () => {
    const langCodes = languages?.map((l) => l.code) ??
      Object.keys(translations).filter((k) => translations[k]?.title);

    const translationsPayload = langCodes
      .map((code) => {
        const t = translations[code] ?? {};
        return {
          languageCode: code,
          title: t.title ?? '',
          body: t.body || undefined,
        };
      })
      .filter((t) => t.title);

    const payload: LegalPageFormData = {
      slug,
      isPublished,
      translations: translationsPayload,
    };

    await onFinish(payload);
  };

  return (
    <div className="space-y-6">
      <FormHeader
        title={isEdit ? 'Редактировать страницу' : 'Создать страницу'}
        onBack={onBack}
        sticky
        actions={
          languages && languages.length > 0 ? (
            <LanguageSwitcher
              languages={languages}
              activeLanguage={activeLanguage}
              onLanguageChange={(code) => setActiveLanguage(code as LanguageCode)}
              fields={TRANSLATION_FIELDS}
              value={translations}
              variant="compact"
            />
          ) : null
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Левая колонка — контент */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Контент
              </CardTitle>
            </CardHeader>
            <CardContent>
              <TranslationsForm
                fields={TRANSLATION_FIELDS}
                value={translations}
                onChange={setTranslations}
                languages={languages}
                activeLanguage={activeLanguage}
                onLanguageChange={(code) => setActiveLanguage(code as LanguageCode)}
              />
            </CardContent>
          </Card>
        </div>

        {/* Правая колонка — настройки */}
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Настройки
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField label="Slug" name="slug">
                <Input
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="privacy-policy"
                  disabled={isEdit}
                />
              </FormField>

              <FormField label="Статус" name="isPublished">
                <Select
                  value={isPublished ? 'published' : 'draft'}
                  onValueChange={(v) => setIsPublished(v === 'published')}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="published">Опубликовано</SelectItem>
                    <SelectItem value="draft">Черновик</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
            </CardContent>
          </Card>
        </div>
      </div>

      <FormSpacer />

      <FormStickyActions>
        <Button onClick={handleSubmit} disabled={formLoading || !slug.trim()}>
          {isEdit ? 'Сохранить' : 'Создать'}
        </Button>
        <Button variant="outline" onClick={onBack}>
          Отмена
        </Button>
      </FormStickyActions>
    </div>
  );
}
